/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps } from 'react'
import { forwardRef } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { Controller, useFormContext, useFormState } from 'react-hook-form'

import { cn } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { Rating, type RatingProps } from '../../ui/rating'

export type RatingFormFieldProps = {
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  ratingProps?: Omit<RatingProps, 'onBlur' | 'onChange' | 'value'>
  required?: boolean
  rules?: RegisterOptions
}

export const RatingFormField = forwardRef<HTMLFieldSetElement, RatingFormFieldProps>((props, forwardedRef) => {
  const { control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const error = errors[props.name]
  const invalid = !!error

  const {
    className: ratingClassName,
    count,
    disabled: ratingDisabled,
    size,
    variant,
    ...restRatingProps
  } = props.ratingProps ?? {}
  const mergedRatingDisabled = props.disabled ?? ratingDisabled

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined ? (
        <FieldLabel className="flex items-center gap-1">
          {props.label}
          {props.required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </FieldLabel>
      ) : null}
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => {
          const value = typeof field.value === 'number' ? field.value : 0

          return (
            <fieldset
              className={cn('m-0 flex min-w-0 flex-col gap-2 border-0 p-0', ratingClassName)}
              ref={forwardedRef}
            >
              <Rating
                className="py-2"
                count={count}
                disabled={mergedRatingDisabled}
                onBlur={field.onBlur}
                onChange={field.onChange}
                size={size}
                value={value}
                variant={variant}
                {...restRatingProps}
              />
            </fieldset>
          )
        }}
        rules={props.rules}
      />
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

RatingFormField.displayName = 'RatingFormField'
